/**
 * MessageNotificationWidget.js
 *
 * Global widget that listens for new incoming messages across all channels
 * and shows visual + audio notifications regardless of the current page.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { socket } from "../socket";

export default function MessageNotificationWidget() {
  const { data: session } = useSession();
  const { t } = useTranslation("common");
  const router = useRouter();

  // Socket state
  const [socketReady, setSocketReady] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [inboxSettings, setInboxSettings] = useState({
    message_notifications_enabled: true,
    message_notification_sound_enabled: true,
  });

  // Audio refs
  const notificationAudioRef = useRef(null);
  const audioContextRef = useRef(null);
  const notificationSequenceRef = useRef(0);
  const notificationTimersRef = useRef(new Map());
  const activeNotificationIdsRef = useRef(new Set());

  // Preload notification sound
  useEffect(() => {
    const audio = new Audio("/sounds/notification.mp3");
    audio.preload = "auto";
    audio.volume = 0.6;
    notificationAudioRef.current = audio;

    // Try to create AudioContext for unlocking
    try {
      audioContextRef.current = new (
        window.AudioContext || window.webkitAudioContext
      )();
    } catch (e) {
      console.log("AudioContext not supported");
    }

    return () => {
      if (notificationAudioRef.current) {
        notificationAudioRef.current.pause();
        notificationAudioRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Unlock audio on any user interaction
  useEffect(() => {
    const unlockAudio = async () => {
      if (audioUnlocked) return;

      try {
        // Resume AudioContext
        if (
          audioContextRef.current &&
          audioContextRef.current.state === "suspended"
        ) {
          await audioContextRef.current.resume();
        }

        // Play silent sound to unlock
        if (notificationAudioRef.current) {
          notificationAudioRef.current.volume = 0;
          await notificationAudioRef.current.play();
          notificationAudioRef.current.pause();
          notificationAudioRef.current.currentTime = 0;
          notificationAudioRef.current.volume = 0.6;
        }

        setAudioUnlocked(true);
      } catch (e) {
        // Ignore - will retry on next interaction
      }
    };

    const events = ["click", "touchstart", "keydown"];
    events.forEach((event) => document.addEventListener(event, unlockAudio));

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, unlockAudio),
      );
    };
  }, [audioUnlocked]);

  // Wait for shared socket to become available/connected
  useEffect(() => {
    if (!session?.user?.currentCompany) return;

    const checkSocket = () => {
      if (socket && socket.connected) {
        setSocketReady(true);
        return true;
      }
      return false;
    };

    if (checkSocket()) return;

    if (socket) {
      const onConnect = () => setSocketReady(true);
      socket.on("connect", onConnect);
      return () => socket.off("connect", onConnect);
    }
  }, [session?.user?.currentCompany]);

  // Preload transfer sound
  const transferAudioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio("/sounds/notification.mp3");
    audio.preload = "auto";
    audio.volume = 0.6;
    transferAudioRef.current = audio;

    return () => {
      if (transferAudioRef.current) {
        transferAudioRef.current.pause();
        transferAudioRef.current = null;
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!inboxSettings.message_notification_sound_enabled) {
      return;
    }

    if (notificationAudioRef.current) {
      notificationAudioRef.current.currentTime = 0;
      notificationAudioRef.current.play().catch(() => {
        // Audio blocked - will play on next user interaction
      });
    }
  }, [inboxSettings.message_notification_sound_enabled]);

  // Play transfer notification sound (slightly different pitch effect)
  const playTransferSound = useCallback(() => {
    if (!inboxSettings.message_notification_sound_enabled) {
      return;
    }

    if (transferAudioRef.current) {
      transferAudioRef.current.currentTime = 0;
      transferAudioRef.current.playbackRate = 1.3;
      transferAudioRef.current.play().catch(() => {});
    }
  }, [inboxSettings.message_notification_sound_enabled]);

  // Dismiss notification and cancel its pending timer. Keeping both the timer
  // and active-id registries outside React state prevents duplicate socket
  // deliveries from immediately recreating a notification that was closed.
  const dismissNotification = useCallback((id) => {
    const timer = notificationTimersRef.current.get(id);
    if (timer) clearTimeout(timer);
    notificationTimersRef.current.delete(id);
    activeNotificationIdsRef.current.delete(id);
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  }, []);

  useEffect(
    () => () => {
      notificationTimersRef.current.forEach((timer) => clearTimeout(timer));
      notificationTimersRef.current.clear();
      activeNotificationIdsRef.current.clear();
    },
    [],
  );

  // Add notification to queue
  const showNotification = useCallback(
    (data, options = {}) => {
      if (!inboxSettings.message_notifications_enabled) {
        return;
      }

      const stableEventId = data?.timestamp
        ? `${data.type || "message"}:${data.channel_id || ""}:${
            data.conversation_id || ""
          }:${data.timestamp}`
        : null;
      const notificationId =
        stableEventId ||
        `notification-${Date.now()}-${++notificationSequenceRef.current}`;

      if (activeNotificationIdsRef.current.has(notificationId)) {
        return;
      }

      const notification = {
        ...data,
        id: notificationId,
      };
      activeNotificationIdsRef.current.add(notificationId);

      setNotifications((prev) => {
        const updated = [notification, ...prev].slice(0, 5);
        const retainedIds = new Set(updated.map((item) => item.id));

        prev.forEach((item) => {
          if (retainedIds.has(item.id)) return;
          const timer = notificationTimersRef.current.get(item.id);
          if (timer) clearTimeout(timer);
          notificationTimersRef.current.delete(item.id);
          activeNotificationIdsRef.current.delete(item.id);
        });

        return updated;
      });

      if (!options.suppressSound) {
        playNotificationSound();
      }

      const timer = setTimeout(
        () => dismissNotification(notification.id),
        5000,
      );
      notificationTimersRef.current.set(notification.id, timer);
    },
    [
      dismissNotification,
      inboxSettings.message_notifications_enabled,
      playNotificationSound,
    ],
  );

  // Navigate to conversation
  const handleNotificationClick = useCallback(
    (notification) => {
      dismissNotification(notification.id);

      // Navigate directly to inbox conversation
      if (notification.conversation_id) {
        router.push(
          `/channels/inbox?tab=chats&convoId=${notification.conversation_id}`,
        );
        return;
      }

      if (notification.channel_id && notification.phone_number) {
        router.push(
          `/channels/inbox?tab=chats&channel_id=${notification.channel_id}&phone=${notification.phone_number}`,
        );
      }
    },
    [dismissNotification, router],
  );

  // Join notification rooms and listen for events
  useEffect(() => {
    if (!socket || !socketReady) return;

    // Join notification rooms for all channels
    socket.emit("joinNotificationRooms");
    socket.emit("loadInboxSettings");

    // Handle reconnection
    const handleReconnect = () => {
      socket.emit("joinNotificationRooms");
      socket.emit("loadInboxSettings");
    };
    socket.on("connect", handleReconnect);

    const handleInboxSettings = (settings) => {
      setInboxSettings({
        message_notifications_enabled: Boolean(
          settings?.message_notifications_enabled,
        ),
        message_notification_sound_enabled: Boolean(
          settings?.message_notification_sound_enabled,
        ),
      });
    };
    socket.on("inboxSettings", handleInboxSettings);

    // Listen for new inbound messages
    const handleNewMessage = (data) => {
      // Don't show notification if already on inbox page viewing this conversation
      const isOnInbox = router.pathname.includes("/channels/inbox");
      const urlParams = new URLSearchParams(window.location.search);
      const currentPhone = urlParams.get("phone");

      if (isOnInbox && currentPhone === data.phone_number) {
        return; // Already viewing this conversation
      }

      showNotification({
        type: "message",
        contact_name: data.contact_name || data.phone_number,
        phone_number: data.phone_number,
        channel_id: data.channel_id,
        conversation_id: data.conversation_id,
        channel_name: data.channel_name,
        message_preview: data.message_preview,
        message_type: data.message_type,
        timestamp: data.timestamp,
      });
    };

    socket.on("newInboundMessage", handleNewMessage);

    const handleConversationTransferred = (data) => {
      if (String(data?.toUserId || "") !== String(session?.user?.id || "")) {
        return;
      }
      playTransferSound();
      showNotification(
        {
          type: "transfer",
          contact_name:
            data.contact_name ||
            t("notifications.conversation-transferred"),
          phone_number: data.phone_number || "",
          channel_id: data.channel_id || "",
          conversation_id: data.conversationId,
          message_preview: t(
            "notifications.conversation-transferred-to-you",
          ),
          timestamp: data.timestamp,
        },
        { suppressSound: true },
      );
    };

    socket.on("conversationTransferred", handleConversationTransferred);

    return () => {
      socket.off("connect", handleReconnect);
      socket.off("newInboundMessage", handleNewMessage);
      socket.off("conversationTransferred", handleConversationTransferred);
      socket.off("inboxSettings", handleInboxSettings);
    };
    // session?.user?.id is a dependency: handleConversationTransferred filters
    // transfers by the current user's id, so the handler must re-bind if the
    // signed-in user changes (otherwise a stale/empty id drops every transfer).
  }, [
    socketReady,
    router,
    showNotification,
    session?.user?.id,
    playTransferSound,
    t,
  ]);

  // Don't render if no session
  if (!session?.user?.currentCompany) return null;

  return (
    <>
      {/* Notification Stack */}
      <div
        style={{
          position: "fixed",
          top: 16,
          right: 20,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          maxWidth: "360px",
          width: "calc(100% - 24px)",
          pointerEvents: "none",
          alignItems: "flex-end",
        }}
      >
        {notifications.map((notification) => (
          <div
            key={notification.id}
            onClick={() => handleNotificationClick(notification)}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              padding: "14px 16px",
              cursor: "pointer",
              pointerEvents: "auto",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              animation: "slideInRight 0.3s ease-out",
              border: "1px solid #e5e7eb",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.02)";
              e.currentTarget.style.boxShadow = "0 6px 24px rgba(0, 0, 0, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow =
                "0 4px 20px rgba(0, 0, 0, 0.15)";
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#25D366",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4px",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1f2937",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {notification.contact_name}
                </span>
                <button
                  type="button"
                  aria-label={t(
                    "notifications.dismiss",
                    "Fechar notificação",
                  )}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissNotification(notification.id);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "2px",
                    cursor: "pointer",
                    color: "#9ca3af",
                    fontSize: "18px",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>

              {notification.channel_name && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  {notification.channel_name}
                </div>
              )}

              <div
                style={{
                  fontSize: "13px",
                  color: "#4b5563",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {notification.message_type === "image"
                  ? `📷 ${t("notifications.image")}`
                  : notification.message_type === "video"
                    ? `🎬 ${t("notifications.video")}`
                    : notification.message_type === "audio"
                      ? `🎵 ${t("notifications.audio")}`
                      : notification.message_type === "document"
                        ? `📄 ${t("notifications.document")}`
                        : notification.message_type === "sticker"
                          ? `🏷️ ${t("notifications.sticker")}`
                          : notification.message_type === "location"
                            ? `📍 ${t("notifications.location")}`
                            : notification.message_preview ||
                              t("notifications.new-message")}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateY(-16px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
